import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { AppHeader } from '@/components/app-header'
import { toast } from 'sonner'
import { CheckCircle2, XCircle, Loader2, Key, MessageSquare } from 'lucide-react'

interface AiConfig {
  api_key_configured: boolean
  api_key_valid: boolean
  last_tested_at: string | null
  clinician_system_prompt: string
  patient_system_prompt: string
}

interface AiSettingsProps {
  config: AiConfig
}

export default function AiSettings({ config }: AiSettingsProps) {
  const [apiKey, setApiKey] = useState('')
  const [testing, setTesting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [clinicianPrompt, setClinicianPrompt] = useState(config.clinician_system_prompt)
  const [patientPrompt, setPatientPrompt] = useState(config.patient_system_prompt)

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token')
    return {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  }

  const handleTestConnection = async () => {
    if (!apiKey) {
      toast.error('Please enter an API key first')
      return
    }

    setTesting(true)
    try {
      const response = await fetch('/admin/ai_settings/test_connection', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ api_key: apiKey })
      })

      if (!response.ok) {
        throw new Error('Failed to test connection')
      }

      const data = await response.json()

      if (data.valid) {
        toast.success('API key is valid!')
      } else {
        toast.error(data.message || 'Invalid API key')
      }
    } catch (error) {
      toast.error('Failed to test connection')
    } finally {
      setTesting(false)
    }
  }

  const handleSaveApiKey = async () => {
    if (!apiKey) {
      toast.error('Please enter an API key')
      return
    }

    setSaving(true)
    try {
      const response = await fetch('/admin/ai_settings/api_key', {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ api_key: apiKey })
      })

      if (!response.ok) {
        throw new Error('Failed to save API key')
      }

      const data = await response.json()

      if (data.success) {
        toast.success('API key saved successfully')
        setApiKey('')
        router.reload()
      } else {
        toast.error('Failed to save API key')
      }
    } catch (error) {
      toast.error('Failed to save API key')
    } finally {
      setSaving(false)
    }
  }

  const handleSavePrompts = async () => {
    setSaving(true)
    try {
      const response = await fetch('/admin/ai_settings/prompts', {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          clinician_system_prompt: clinicianPrompt,
          patient_system_prompt: patientPrompt
        })
      })

      if (!response.ok) {
        throw new Error('Failed to update prompts')
      }

      const data = await response.json()

      if (data.success) {
        toast.success('System prompts updated successfully')
      } else {
        toast.error('Failed to update prompts')
      }
    } catch (error) {
      toast.error('Failed to update prompts')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <AppHeader
        breadcrumbs={[
          { label: 'Admin Panel', href: '/admin/console' },
          { label: 'AI Settings' }
        ]}
      />

      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* API Key Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              <CardTitle>OpenAI API Configuration</CardTitle>
            </div>
            <CardDescription>
              Configure your OpenAI API key for ChatGPT integration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Status:</span>
                {config.api_key_configured ? (
                  <div className="flex items-center gap-1 text-green-600">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>API Key Configured</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-orange-600">
                    <XCircle className="h-4 w-4" />
                    <span>No API Key</span>
                  </div>
                )}
              </div>
              {config.last_tested_at && (
                <span className="text-muted-foreground text-sm">
                  • Last tested: {new Date(config.last_tested_at).toLocaleString()}
                </span>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="api_key">API Key</Label>
              <div className="flex gap-2">
                <Input
                  id="api_key"
                  type="password"
                  placeholder="sk-proj-..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="font-mono"
                />
                <Button
                  variant="outline"
                  onClick={handleTestConnection}
                  disabled={testing || !apiKey}
                >
                  {testing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Test
                </Button>
                <Button onClick={handleSaveApiKey} disabled={saving || !apiKey}>
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                Enter your OpenAI API key. The key will be encrypted before storing.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* System Prompts */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              <CardTitle>System Prompts</CardTitle>
            </div>
            <CardDescription>
              Configure AI behavior for clinician and patient portals
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="clinician_prompt">Clinician Portal System Prompt</Label>
              <Textarea
                id="clinician_prompt"
                rows={6}
                value={clinicianPrompt}
                onChange={(e) => setClinicianPrompt(e.target.value)}
                placeholder="You are a helpful AI assistant for healthcare clinicians..."
              />
              <p className="text-muted-foreground text-sm">
                This prompt guides the AI when assisting clinicians.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="patient_prompt">Patient Portal System Prompt</Label>
              <Textarea
                id="patient_prompt"
                rows={6}
                value={patientPrompt}
                onChange={(e) => setPatientPrompt(e.target.value)}
                placeholder="You are a helpful AI assistant for patients..."
              />
              <p className="text-muted-foreground text-sm">
                This prompt guides the AI when assisting patients.
              </p>
            </div>

            <div className="flex justify-end">
              <Button onClick={handleSavePrompts} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save System Prompts
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
